import React, { createContext, useContext, useState, useEffect, useLayoutEffect } from "react";
import { Keyword, Article } from "@/types";

const LS_KEYWORDS = "seo_keywords";
const LS_ARTICLES = "seo_articles";

function lsRead<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function lsWrite<T>(key: string, value: T): void {
  if (typeof window === "undefined") return;
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
}

const SEED_KEYWORDS: Keyword[] = [
  { id: "k1", raw: "pinjaman online", keyword: "cara pinjaman online aman terpercaya", intentBoard: "edukasi", source: "seo", volume: 12000, difficulty: 45, createdAt: "2026-03-01" },
  { id: "k2", raw: "kredit usaha", keyword: "kredit usaha kecil bunga rendah OJK", intentBoard: "konversi", source: "seo", volume: 8500, difficulty: 52, createdAt: "2026-03-02" },
  { id: "k3", raw: "pinjol ilegal", keyword: "ciri-ciri pinjol ilegal cara lapor OJK", intentBoard: "defensif", source: "seo", volume: 22000, difficulty: 38, createdAt: "2026-03-03" },
  { id: "k4", raw: "cicilan motor", keyword: "cicilan motor tanpa DP 2026 syarat mudah", intentBoard: "solusi", source: "sem", volume: 6200, difficulty: 61, createdAt: "2026-03-05" },
  { id: "k5", raw: "bunga KTA", keyword: "perbandingan bunga KTA bank vs fintech 2026", intentBoard: "kompetitor", source: "seo", volume: 9100, difficulty: 55, createdAt: "2026-03-07" },
];

const SEED_ARTICLES: Article[] = [
  {
    id: "a1",
    title: "Cara Aman Pinjaman Online Terpercaya 2026",
    keyword: "cara pinjaman online aman terpercaya",
    intentBoard: "edukasi",
    content: "# Cara Aman Pinjaman Online Terpercaya 2026\n\nMemilih platform pinjaman online yang aman...",
    status: "published",
    createdAt: "2026-03-10",
    wordCount: 1850,
  },
  {
    id: "a2",
    title: "Waspada Pinjol Ilegal: Ciri dan Cara Lapor OJK",
    keyword: "ciri-ciri pinjol ilegal cara lapor OJK",
    intentBoard: "defensif",
    content: "# Waspada Pinjol Ilegal\n\nPinjaman online ilegal semakin marak...",
    status: "draft",
    createdAt: "2026-03-12",
    wordCount: 2100,
  },
];

interface GlobalContextValue {
  keywords: Keyword[];
  setKeywords: React.Dispatch<React.SetStateAction<Keyword[]>>;
  articles: Article[];
  setArticles: React.Dispatch<React.SetStateAction<Article[]>>;
}

const GlobalContext = createContext<GlobalContextValue>({
  keywords: [],
  setKeywords: () => {},
  articles: [],
  setArticles: () => {},
});

export function GlobalProvider({ children }: { children: React.ReactNode }) {
  const [keywords, setKeywords] = useState<Keyword[]>(SEED_KEYWORDS);
  const [articles, setArticles] = useState<Article[]>(SEED_ARTICLES);
  const [hydrated, setHydrated] = useState(false);

  // Restore before paint so the first passive auto-save never writes seed over real LS data.
  useLayoutEffect(() => {
    const savedKws = lsRead<Keyword[]>(LS_KEYWORDS, []);
    const savedArts = lsRead<Article[]>(LS_ARTICLES, []);
    if (savedKws.length > 0) setKeywords(savedKws);
    if (savedArts.length > 0) setArticles(savedArts);
    setHydrated(true);
  }, []);

  // Auto-persist on every change (only after hydration)
  useEffect(() => { if (hydrated) lsWrite(LS_KEYWORDS, keywords); }, [keywords, hydrated]);
  useEffect(() => { if (hydrated) lsWrite(LS_ARTICLES, articles); }, [articles, hydrated]);

  return (
    <GlobalContext.Provider value={{ keywords, setKeywords, articles, setArticles }}>
      {children}
    </GlobalContext.Provider>
  );
}

export function useGlobalContext() {
  return useContext(GlobalContext);
}
